import type { StringKey } from './strings';
import type { Ride, RideStatus } from './types';

/** Which live screen a ride status shows. */
export type LiveScreen = 'finding' | 'arriving' | 'ontrip' | 'arrived' | 'ended';

export function screenFor(status: RideStatus): LiveScreen {
  switch (status) {
    case 'REQUESTED':
    case 'OFFERING':
      return 'finding';
    case 'ACCEPTED':
    case 'ARRIVED':
      return 'arriving';
    case 'STARTED':
      return 'ontrip';
    case 'COMPLETED':
      return 'arrived';
    default:
      return 'ended';
  }
}

export const OPEN: RideStatus[] = ['REQUESTED', 'OFFERING', 'ACCEPTED', 'ARRIVED', 'STARTED'];
export const isOpen = (s: RideStatus) => OPEN.includes(s);
export const canCancel = (s: RideStatus) => ['REQUESTED', 'OFFERING', 'ACCEPTED', 'ARRIVED'].includes(s);

/** Which words to show for a ride that ended without a trip (keys in strings.ts). */
export function endedMessage(r: Pick<Ride, 'status' | 'cancellationFeePaise'>): { title: StringKey; body: StringKey | null } {
  switch (r.status) {
    case 'EXPIRED':
    case 'CANCELLED_BY_DRIVER':
    case 'CANCELLED_BY_ADMIN':
      return { title: `ended.${r.status}.title`, body: `ended.${r.status}.body` };
    case 'CANCELLED_BY_RIDER':
      return { title: 'ended.CANCELLED_BY_RIDER.title', body: r.cancellationFeePaise > 0 ? 'ended.fee' : 'ended.noCharge' };
    default:
      return { title: 'ended.title', body: null };
  }
}

/**
 * Which ride the app should show after a refresh: the open ride if there is one, else a just-completed one the
 * rider has not finished paying/rating for (kept by id in storage).
 */
export function pickLive(current: Ride | null, pendingFinish: Ride | null): Ride | null {
  if (current) return current;
  if (pendingFinish && pendingFinish.status === 'COMPLETED') return pendingFinish;
  return null;
}

/** "Cancel ride · free" / "free until 6:45 pm" / "₹20 fee" wording inputs. */
export function cancelTerms(
  r: Pick<Ride, 'status' | 'acceptedAt'>,
  s: { cancellationFeePaise: number; cancellationGraceSeconds: number } | null,
  now = Date.now(),
): { free: boolean; until: Date | null; feePaise: number } {
  if (!s || s.cancellationFeePaise <= 0) return { free: true, until: null, feePaise: 0 };
  if (!r.acceptedAt || r.status === 'REQUESTED' || r.status === 'OFFERING') return { free: true, until: null, feePaise: 0 };
  const until = new Date(Date.parse(r.acceptedAt) + s.cancellationGraceSeconds * 1000);
  return { free: now < until.getTime(), until, feePaise: s.cancellationFeePaise };
}
